import React from "react";
import { useAdminGetJobsQuery } from "@diditusa/shared/api/adminApi";
import { useLoadUserQuery } from "@diditusa/shared/api/sessionApi";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import BasicTable from "@diditusa/shared/web_components/BasicTable/BasicTable";
import { JobStatus } from "@diditusa/shared/types/jobTypes";
import { BasicTableHeader } from "@diditusa/shared/types/commonTypes";
import { format } from "date-fns";
import { getErrorMessage } from "@diditusa/shared/api/apiUtils";
import { DateTime } from "luxon";

interface Props {
  statuses?: JobStatus[];
}
export default function Jobs({ statuses }: Props) {
  const { data: jobs, ...getJobsState } = useAdminGetJobsQuery({
    statusFilter: statuses,
  });
  const navigate = useNavigate();
  const { data: user, ...loadUserState } = useLoadUserQuery();

  if (getJobsState.isLoading) {
    return <LoadingIndicator isVisible={getJobsState.isLoading} />;
  }

  if (getJobsState.isError) {
    return <p>Error fetching jobs {getErrorMessage(getJobsState.error)}</p>;
  }

  interface JobDetails {
    id: number;
    reservationCode: string;
    user: string;
    location: string;
    phoneNumber: string;
    agents: string;
    startDate: DateTime;
    startTime: DateTime;
    duration: number;
    package: string;
    status: JobStatus;
  }

  type JobDetailsKey = keyof JobDetails;
  const jobDetails: JobDetails[] =
    jobs?.map((j) => ({
      id: j.id,
      reservationCode: j.reservationCode,
      user: j.createdBy
        ? `${j.createdBy.firstName} ${j.createdBy.lastName}`
        : j.createdById,
      phoneNumber: j.createdBy ? `${j.createdBy?.phoneNumber}` : "-",
      company: j.company
        ? j.company.name
        : j.companyId
        ? j.companyId
        : "Unassigned",
      agents:
        j.jobAssignments
          ?.map((ja) =>
            ja.user ? `${ja.user.firstName} ${ja.user.lastName}` : ja.userId
          )
          .join(",") || "",
      location: j.location,
      startTime: j.startTime,
      startDate: j.startTime,
      duration: j.durationHours,
      package: j.securityOption?.title || "",
      status: j.status,
    })) || [];

  const headers: BasicTableHeader<JobDetailsKey>[] = [
    { key: "reservationCode", value: "Reservation Number" },
    { key: "user", value: "Customer" },
    { key: "phoneNumber", value: "Phone Number" },
    { key: "agents", value: "Assigned Agent" },
    { key: "location", value: "Location" },
    { key: "startDate", value: "Start Date" },
    { key: "startTime", value: "Start Time" },
    { key: "duration", value: "Duration" },
    { key: "package", value: "Package" },
    { key: "status", value: "Status" },
  ];

  const onRowClick = (idx: number) => {
    navigate(`/jobs/${jobDetails[idx].id}`);
  };

  const getCustomComponentForCell = (field: JobDetailsKey, idx: number) => {
    const c = jobDetails[idx];
    if (field === "startDate") {
      return c.startDate.toFormat("MM/dd/yyyy");
    }

    if (field === "startTime") {
      return c.startTime.toFormat("hh:mm a ZZZZ");
    }
  };

  return (
    <Box component="main" sx={{ flexGrow: 1 }}>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Jobs
          </Typography>
        </Box>
        <BasicTable
          headers={headers}
          data={jobDetails}
          getCustomComponentForField={getCustomComponentForCell}
          onRowClick={onRowClick}
        />
      </Box>
    </Box>
  );
}
